import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable } from 'rxjs';
import { environment } from '../../environments/environment';
import { map } from 'rxjs/operators';

export interface AppFile {
  name: string;
  path: string;
  size: number;
  updated: string;
  type?: string; // pdf, docx, xlsx, image, etc.
}

export interface FileDetails {
  link: string;
  filename?: string;
  created_at: string;
  modified_at: string;
  thumbnail?: {
    url: string;
    filename: string;
    created_at: string;
    modified_at: string;
  };
}

export interface YouTubeDetails {
  video_url: string;
  video_id: string;
  title: string;
  description: string;
  duration?: string | null;
  thumbnail_url: string;
  embed_url: string;
  watch_url: string;
  created_at: string;
  modified_at: string;
  has_video: boolean;
}

export interface FilesResponse {
  id: number;
  subject: string;
  section: string;
  topic: string;
  subtopic: string;
  files: {
    txt?: FileDetails;
    docx?: FileDetails;
    pdf?: FileDetails;
    xlsx?: FileDetails;
    png?: FileDetails;
    html?: FileDetails;
    png_thumbnail?: FileDetails;
    gif?: FileDetails;
    [key: string]: FileDetails | undefined;
  };
  youtube?: YouTubeDetails;
}

export interface FileBundlesFilterResponse {
  filters: {
    subject: string;
    section: string;
  };
  bundles: FilesResponse[];
  count: number;
}

@Injectable({
  providedIn: 'root'
})
export class FileService {
  private baseUrl = environment.apiBaseUrl;
  private fileBundleUrl = `${this.baseUrl}/api/file-bundles`;
  private defaultBundleId = 1; // Default bundle ID

  constructor(private http: HttpClient) { }

  // Get list of files from GCP bucket (legacy method)
  getFiles(): Observable<AppFile[]> {
    return this.http.get<AppFile[]>(`${this.baseUrl}/api/files`);
  }

  // Download file by name (legacy method)
  downloadFile(fileName: string): Observable<Blob> {
    return this.http.get(`${this.baseUrl}/api/files/download/${fileName}`, {
      responseType: 'blob'
    });
  }

  // Download file using database reference (legacy method)
  downloadFileFromDb(fileId: string | number): Observable<Blob> {
    return this.http.get(`${this.baseUrl}/api/files/db-download/${fileId}`, {
      responseType: 'blob'
    });
  }
  
  // Get list of files filtered by type (legacy method)
  getFilesByType(fileType: string): Observable<AppFile[]> {
    return this.http.get<AppFile[]>(`${this.baseUrl}/api/files/type/${fileType}`);
  }

  // Get file bundle by ID
  getFileBundle(bundleId: number = this.defaultBundleId): Observable<FilesResponse> {
    return this.http.get<FilesResponse>(`${this.fileBundleUrl}/${bundleId}`);
  }

  // Get all file bundles
  getAllFileBundles(): Observable<FilesResponse[]> {
    return this.http.get<{bundles: FilesResponse[], count: number}>(`${this.fileBundleUrl}`).pipe(
      map(response => {
        console.log('Raw API response:', response);
        // Check if response has bundles property which is an array
        if (response && typeof response === 'object' && 'bundles' in response && Array.isArray(response.bundles)) {
          console.log('API returned bundles array with', response.bundles.length, 'items');
          return response.bundles;
        }
        
        // Handle case where API returns an object instead of array with bundles
        if (response && !Array.isArray(response)) {
          console.log('API returned object instead of array, attempting to extract bundles');
          
          // If it's a single object that looks like a FilesResponse, wrap it in array
          if (typeof response === 'object' && 'id' in response) {
            return [response as unknown as FilesResponse];
          }
          // If it's some other object format, log and return empty array
          console.error('Unexpected API response format:', response);
          return [];
        }
        
        return response as unknown as FilesResponse[];
      })
    );
  }
  
  // Filter file bundles by subject and section
  filterFileBundles(subject: string, section: string): Observable<FilesResponse[]> {
    const filters = { subject, section };
    return this.http.post<FileBundlesFilterResponse>(`${this.fileBundleUrl}/filter`, { filters }).pipe(
      map(response => {
        console.log('Filter API response:', response);
        if (response && 'bundles' in response && Array.isArray(response.bundles)) {
          console.log('Filter API returned bundles array with', response.bundles.length, 'items');
          return response.bundles;
        }
        console.error('Unexpected filter API response format:', response);
        return [];
      })
    );
  }
  
  // Download file using the proxy endpoint (to avoid CORS)
  downloadFileFromUrl(url: string): Observable<Blob> {
    // Use the new proxy endpoint instead of direct URL access
    const encodedUrl = encodeURIComponent(url);
    return this.http.get(`${this.baseUrl}/api/files/proxy-download?url=${encodedUrl}`, {
      responseType: 'blob'
    });
  }
  
  // Download file by type from the bundle using the dedicated endpoint
  downloadFileByType(bundleId: number, fileType: string): Observable<Blob> {
    return this.http.get(`${this.fileBundleUrl}/${bundleId}/download/${fileType}`, {
      responseType: 'blob'
    });
  }
  
  // Upload a new file to the bundle
  uploadFileToBundle(bundleId: number, fileType: string, file: FormData): Observable<any> {
    return this.http.post(`${this.fileBundleUrl}/${bundleId}/upload/${fileType}`, file);
  }
  
  // Replace an existing file in the bundle
  replaceFileInBundle(bundleId: number, fileType: string, file: FormData): Observable<any> {
    return this.http.put(`${this.fileBundleUrl}/${bundleId}/replace/${fileType}`, file);
  }
  
  // Delete a file from the bundle
  deleteFileFromBundle(bundleId: number, fileType: string): Observable<any> {
    return this.http.delete(`${this.fileBundleUrl}/${bundleId}/delete/${fileType}`);
  }
  
  // Create a new file bundle with multiple files
  createFileBundle(formData: FormData): Observable<any> {
    return this.http.post(`${this.fileBundleUrl}/create`, formData);
  }

  // ===== UI/UX refresh helpers =====

  // True when any file key in the bundle ends with "_hindi".
  bundleHasHindi(bundle: FilesResponse): boolean {
    if (!bundle?.files) return false;
    return Object.keys(bundle.files).some(k => /_hindi$/i.test(k) && !!bundle.files[k]);
  }

  // Resolve `fileType` for a language: returns the existing file key, or null.
  // Hindi tries `${fileType}_hindi` first, falls back to base; English just checks base.
  resolveFileKey(bundle: FilesResponse, fileType: string, lang: 'en' | 'hi'): string | null {
    if (!bundle?.files) return null;
    if (lang === 'hi') {
      const hi = `${fileType}_hindi`;
      if (bundle.files[hi]?.filename) return hi;
    }
    return bundle.files[fileType]?.filename ? fileType : null;
  }

  // Tab strip for the mindmap viewer. Returns formats that actually have content for the language.
  availableFormats(
    bundle: FilesResponse,
    lang: 'en' | 'hi'
  ): Array<'html' | 'imindmap' | 'pdf' | 'png' | 'txt' | 'youtube'> {
    const out: Array<'html' | 'imindmap' | 'pdf' | 'png' | 'txt' | 'youtube'> = [];
    const has = (t: string) => !!this.resolveFileKey(bundle, t, lang);
    if (has('html'))     out.push('html');
    if (has('imindmap')) out.push('imindmap'); // future
    if (has('pdf'))      out.push('pdf');
    if (has('png'))      out.push('png');
    if (has('txt'))      out.push('txt');
    if (bundle.youtube?.has_video && bundle.youtube.embed_url) out.push('youtube');
    return out;
  }

  // Best thumbnail URL for a card preview. Falls back through the two thumbnail sources, then the full PNG.
  getThumbnailUrl(bundle: FilesResponse): string | null {
    if (!bundle?.files) return null;
    return (
      bundle.files['png_thumbnail']?.link ??
      bundle.files['png']?.thumbnail?.url ??
      bundle.files['png']?.link ??
      null
    );
  }
} 